import Link from '@gik/core/components/Link.tsx';
import type { WordpressTaxonomy } from '@gik/core/models/wordpress/WordpressTaxonomy';
import routes from '@gik/core/routes';
import type { UIComponent } from '@gik/core/types/UI';
import { useBemCN } from '@gik/core/utils/bemBlock';
import withComponentErrorBoundary from '@gik/core/utils/withComponentErrorBoundary';
import { Button } from '@gik/ui-legacy/Button';
import { Grid } from '@gik/ui-legacy/Grid';
import React from 'react';
import { GiftTypeTile } from './GiftTypeTile';

export type GiftTypeGridProps = {
  // can pass initial or mock data
  items?: WordpressTaxonomy[];
  cols?: number;
  initialItemNum?: number;
  loadMoreButton?: boolean;
  skeleton?: boolean;
  skeletonItems?: number;
  titleInsideBox?: boolean;
} & UIComponent;

function GiftTypeGridComp({
  initialItemNum = 12,
  cols = 5,
  loadMoreButton,
  items,
  className,
}: GiftTypeGridProps): React.ReactElement {
  const bem = useBemCN('gift-type-grid');

  const taxonomyType = 'category';

  const [page, setPage] = React.useState<number>(1);
  const totalPages = 10;

  function handleLoadMore() {
    // TODO: this should perform backend pagination when our rest api and cache server supports it
    // for now just show all situations in one go and hide the load more button
    setPage(totalPages);
  }

  function getLinkUrl(item: WordpressTaxonomy) {
    return `/${taxonomyType}/${item.slug}`;
  }

  if (items?.length === 0) {
    return <div {...bem('empty')}>Empty</div>;
  }

  return (
    <div {...bem(null, null, className)}>
      <Grid cols={cols} {...bem('grid')}>
        {items.map((item, index) => {
          if (item.slug === 'uncategorized') return null;
          if (index >= page * initialItemNum) return null;
          return (
            <Link key={index} href={getLinkUrl(item)} scroll shallow {...bem('item')}>
              <GiftTypeTile title={item.name} imageUrl={item.acf.tax_thumb_image} />
            </Link>
          );
        })}
      </Grid>

      {loadMoreButton && page < totalPages && (
        <Button onClick={handleLoadMore} variant="default" pill>
          Show All
        </Button>
      )}
    </div>
  );
}
export const GiftTypeGrid = withComponentErrorBoundary(GiftTypeGridComp);

export const GiftTypeGridSkeleton = ({
  skeletonItems = 12,
  cols = 5,
  loadMoreButton,
}: Partial<GiftTypeGridProps>): React.ReactElement => {
  const bem = useBemCN(`gift-type-grid`);
  return (
    <div {...bem()}>
      <Grid cols={cols} {...bem('grid')}>
        {Array.from(Array(skeletonItems).keys()).map(index => {
          return <GiftTypeTile skeleton key={index} />;
        })}
      </Grid>
      {loadMoreButton && (
        <Button variant="default" pill disabled>
          Show All
        </Button>
      )}
    </div>
  );
};
